/**
 * First-run / no-SED landing for the Site Evidence tab.
 */
import React from 'react';
import { Database, RefreshCw, Sparkles } from 'lucide-react';

export interface SedRebuildLandingCardProps {
  sedStatus?: string;
  isRebuilding?: boolean;
  onRebuild: () => void;
}

export const SedRebuildLandingCard: React.FC<SedRebuildLandingCardProps> = ({
  sedStatus,
  isRebuilding = false,
  onRebuild,
}) => {
  const failed = sedStatus === 'failed';

  return (
    <div
      className="rounded-xl border border-border bg-card shadow-sm p-8 max-w-2xl mx-auto text-center space-y-6"
      data-testid="sed-rebuild-landing"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
        <Database className="w-7 h-7 text-primary" aria-hidden />
      </div>
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">
          {failed ? 'Rebuild your Site Evidence Document' : 'Build your Site Evidence Document'}
        </h3>
        <p className="text-sm text-text-muted leading-relaxed max-w-md mx-auto">
          The SED is the authoritative factual record for this site — provider data, uploaded
          documents, and your amendments. Reports, the Coverage Matrix, and extracted variables
          all read from it.
        </p>
      </div>
      <ul className="text-left text-[11px] text-text-secondary space-y-2 max-w-sm mx-auto">
        <li className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
          <span>Auto-collect climate, grid, flood, seismic, and parcel data from public APIs</span>
        </li>
        <li className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
          <span>Power the Coverage Matrix and report generation with one source of truth</span>
        </li>
      </ul>
      <button
        type="button"
        onClick={onRebuild}
        disabled={isRebuilding}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="sed-rebuild-landing-cta"
      >
        <RefreshCw className={`w-4 h-4 ${isRebuilding ? 'animate-spin' : ''}`} aria-hidden />
        {isRebuilding ? 'Building evidence…' : failed ? 'Retry rebuild' : 'Rebuild SED'}
      </button>
      {failed && !isRebuilding && (
        <p className="text-[10px] text-warning">
          The previous bootstrap did not complete. Rebuild to try again from scratch.
        </p>
      )}
    </div>
  );
};
